import { useCallback, useEffect, useRef } from 'react';
import { withWindow } from '@shared/utils/withWindow';

type AnimationFrameCallback = () => void;

type ScheduleAnimationFrame = (callback: AnimationFrameCallback, frameCount?: number) => () => void;

export const useRequestAnimationFrame = (): ScheduleAnimationFrame => {
  const frameIdsRef = useRef<ReadonlyArray<number>>([]);

  const cancelScheduledFrames = useCallback(() => {
    withWindow(global => {
      frameIdsRef.current.forEach(frameId => global.cancelAnimationFrame(frameId));
      frameIdsRef.current = [];
    });
  }, []);

  useEffect(() => {
    return cancelScheduledFrames;
  }, [cancelScheduledFrames]);

  return useCallback<ScheduleAnimationFrame>((callback, frameCount = 1) => {
    let isCancelled = false;
    const scheduledFrameIds: number[] = [];

    const removeFrameId = (frameId: number) => {
      frameIdsRef.current = frameIdsRef.current.filter(id => id !== frameId);
    };

    const scheduleFrame = (remainingFrames: number) => {
      const frameId = withWindow(
        global =>
          global.requestAnimationFrame(() => {
            removeFrameId(frameId);

            if (isCancelled) {
              return;
            }

            if (remainingFrames <= 1) {
              callback();
              return;
            }

            scheduleFrame(remainingFrames - 1);
          }),
        0,
      );

      frameIdsRef.current = [...frameIdsRef.current, frameId];
      scheduledFrameIds.push(frameId);
    };

    scheduleFrame(frameCount);

    return () => {
      isCancelled = true;
      withWindow(global => {
        scheduledFrameIds.forEach(frameId => {
          global.cancelAnimationFrame(frameId);
          removeFrameId(frameId);
        });
      });
    };
  }, []);
};
